import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ChangePasswordModal from '../components/ChangePasswordModal'; // We'll create this next

// Define the User type based on your API response
interface User {
  id: number | string;
  username: string;
  email?: string;
  role?: string; // Add role if available
}

const SettingsPage: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch user data when the page loads
  useEffect(() => {
    const fetchUserDetails = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        console.error("No token found, cannot fetch user details.");
        setIsLoading(false);
        // Optionally, redirect to login here
        return;
      }

      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL;
        const response = await axios.get(`${API_BASE_URL}/api/users/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setCurrentUser(response.data);
      } catch (error) {
        console.error("Failed to fetch user details:", error);
        // Handle error (e.g., if token is expired, trigger logout)
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserDetails();
  }, []); // Empty array ensures this runs once on mount

  // --- Styles ---
  const styles: { [key: string]: React.CSSProperties } = {
    page: { padding: '2rem', backgroundColor: '#f1f5f9', minHeight: '100vh' },
    title: { margin: '0 0 2rem 0', fontSize: '1.75rem', fontWeight: 600, color: '#1e293b' },
    card: { backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0', marginBottom: '2rem', overflow: 'hidden' },
    cardHeader: { padding: '1rem 1.5rem', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '1rem', fontWeight: 600, color: '#334155' },
    cardBody: { padding: '1.5rem' },
    detailRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #f1f5f9' },
    detailLabel: { fontSize: '0.9rem', color: '#64748b', fontWeight: 500 },
    detailValue: { fontSize: '0.9rem', color: '#1e293b', fontWeight: 600 },
    passwordValue: { fontFamily: 'monospace', letterSpacing: '2px' },
    buttonContainer: { display: 'flex', justifyContent: 'flex-end', paddingTop: '1rem'},
    button: { backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 1.2rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' },
  };

  const handlePasswordChangeSuccess = () => {
    setIsModalOpen(false);
    alert("Password changed successfully!!");
  };

  if (isLoading) {
    return (
      <div style={styles.page}>
        <h1 style={styles.title}>Account Settings</h1>
        <p>Loading user data...</p>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={styles.page}>
        <h1 style={styles.title}>Account Settings</h1>
        <p>Could not load user data. Please try logging in again.</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Account Settings</h1>

      {/* User Profile Card */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>User Profile</div>
        <div style={styles.cardBody}>
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>User ID</span>
            <span style={styles.detailValue}>{currentUser.id}</span>
          </div>
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Email</span>
            <span style={styles.detailValue}>{currentUser.email || currentUser.username}</span>
          </div>
          {currentUser.role && (
            <div style={{...styles.detailRow, borderBottom: 'none', marginBottom: 0}}>
              <span style={styles.detailLabel}>Role</span>
              <span style={styles.detailValue}>{currentUser.role}</span>
            </div>
          )}
        </div>
      </div>

      {/* Security Card */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>Security</div>
        <div style={styles.cardBody}>
           <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Password</span>
            <span style={{...styles.detailValue, ...styles.passwordValue}}>••••••••</span>
          </div>
          <div style={styles.buttonContainer}>
             <button style={styles.button} onClick={() => setIsModalOpen(true)}>
               Change Password
             </button>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      <ChangePasswordModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handlePasswordChangeSuccess}
      />
    </div>
  );
};

export default SettingsPage;